import React, { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, Drawer, Typography } from '@mui/material';
import { MoreHoriz as MoreIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { useBrand } from '../../context/BrandContext';
import { getNavigation, flattenNavigation, findActivePath } from '../../constants/navigation';

// Phone-only tab bar (hidden from the md breakpoint up, where the sidebar
// takes over). The everyday destinations sit in the bar; everything else in
// the nav config folds into a "More" bottom sheet, so it stays in sync with
// the sidebar and the top-bar quick-jump search automatically.
const PRIMARY_WITH_BRAND = ['Dashboard', 'Composer', 'Calendar', 'Analytics'];
const PRIMARY_NO_BRAND = ['Dashboard', 'Brands'];

const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedBrand } = useBrand();
  const [moreOpen, setMoreOpen] = useState(false);

  // Measure the bar so the "More" sheet sits exactly above it (safe-area
  // padding included) instead of behind it.
  const barRef = useRef(null);
  const [barHeight, setBarHeight] = useState(56);
  useEffect(() => {
    const el = barRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => setBarHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!user) return null;

  const nav = getNavigation({ selectedBrand, user });
  const all = flattenNavigation(nav);
  const primaryNames = selectedBrand ? PRIMARY_WITH_BRAND : PRIMARY_NO_BRAND;
  const primary = primaryNames.map((n) => all.find((i) => i.text === n)).filter(Boolean);
  const rest = all.filter((i) => !primaryNames.includes(i.text));
  const activePath = findActivePath(location.pathname, all);
  const restActive = rest.some((i) => i.path === activePath);

  const groups = rest.reduce((acc, item) => {
    const g = item.group === 'Main' ? 'Workspace' : item.group;
    (acc[g] = acc[g] || []).push(item);
    return acc;
  }, {});

  const go = (path) => {
    setMoreOpen(false);
    navigate(path);
  };

  const icon = (item) => React.cloneElement(item.icon, { fontSize: 'small' });
  const tabSx = (on) => ({
    flex: 1,
    minWidth: 0,
    flexDirection: 'column',
    gap: '2px',
    py: 1,
    color: on ? 'primary.main' : 'text.secondary',
  });
  const labelSx = { fontSize: '0.6875rem', fontWeight: 600, lineHeight: 1 };

  return (
    <>
      <Box
        ref={barRef}
        component="nav"
        aria-label="Primary"
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: (theme) => theme.zIndex.drawer + 2,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          pb: 'env(safe-area-inset-bottom)',
        }}
      >
        {primary.map((item) => {
          const active = item.path === activePath;
          return (
            <ButtonBase
              key={item.path}
              onClick={() => go(item.path)}
              aria-label={item.text}
              aria-current={active ? 'page' : undefined}
              sx={tabSx(active)}
            >
              {icon(item)}
              <Typography noWrap sx={labelSx}>{item.text}</Typography>
            </ButtonBase>
          );
        })}
        <ButtonBase
          onClick={() => setMoreOpen((v) => !v)}
          aria-label="More"
          aria-expanded={moreOpen}
          sx={tabSx(moreOpen || restActive)}
        >
          <MoreIcon fontSize="small" />
          <Typography noWrap sx={labelSx}>More</Typography>
        </ButtonBase>
      </Box>

      <Drawer
        anchor="bottom"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            bottom: `${barHeight}px`,
            borderRadius: '12px 12px 0 0',
            maxHeight: `calc(75vh - ${barHeight}px)`,
            zIndex: (theme) => theme.zIndex.drawer + 3,
          },
        }}
      >
        <Box sx={{ p: 1.5, overflowY: 'auto' }}>
          <Box sx={{ width: 32, height: 4, borderRadius: 2, bgcolor: 'divider', mx: 'auto', mb: 1.5 }} />
          {Object.entries(groups).map(([title, items]) => (
            <Box key={title} sx={{ mb: 1 }}>
              <Typography
                variant="overline"
                sx={{ display: 'block', px: 0.5, mb: 0.5, lineHeight: '16px', fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary' }}
              >
                {title}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.75 }}>
                {items.map((item) => {
                  const current = item.path === activePath;
                  return (
                    <ButtonBase
                      key={item.path}
                      onClick={() => go(item.path)}
                      aria-label={item.text}
                      aria-current={current ? 'page' : undefined}
                      sx={{
                        flexDirection: 'column',
                        gap: 0.5,
                        py: 1,
                        px: 0.25,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: current ? 'primary.main' : 'divider',
                        bgcolor: current ? 'action.selected' : 'transparent',
                        color: 'primary.main',
                      }}
                    >
                      {icon(item)}
                      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, textAlign: 'center', lineHeight: 1.25, color: 'text.primary' }}>
                        {item.text}
                      </Typography>
                    </ButtonBase>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      </Drawer>
    </>
  );
};

export default MobileBottomNav;
